import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import roleAdminService from '../../services/roleAdminService';
import { CONTENITORE_TABELLA, TABELLA_PAGINA, TESTATA_TABELLA } from '../common/stiliTabella';

/**
 * Tab «Utenti assegnati» del dettaglio ruolo (spec §10.3).
 *
 * Elenco in sola lettura: chi ha questo ruolo si vede da `GET /v1/roles/{id}/users`, ma le
 * assegnazioni si cambiano dal tab Utenti della pagina, non da qui — questo pannello non ha
 * un'azione di rimozione, solo il collegamento per andare a farla dove va fatta.
 *
 * Stessa forma di `UsersTab`: il caricamento si dichiara, l'errore si dichiara con un
 * `Alert` e un pulsante «Riprova», il caso vuoto si dichiara col suo testo — non si mima
 * nessuno dei tre con una tabella indistinguibile dagli altri due stati.
 */
export default function RoleUsersPanel({ roleId }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        roleAdminService
            .listRoleUsers(roleId)
            .then((data) => {
                setUsers(data || []);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, [roleId]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Le assegnazioni si cambiano dal tab{' '}
                <Link component={RouterLink} to="/roles-and-permissions?tab=utenti">
                    Utenti
                </Link>
                , non da qui.
            </Typography>

            {error && (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={load}>
                            Riprova
                        </Button>
                    }
                >
                    Non è stato possibile caricare gli utenti assegnati.
                </Alert>
            )}

            {!error && loading && (
                <Typography variant="body2" color="text.secondary">
                    Caricamento…
                </Typography>
            )}

            {!error && !loading && users.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Nessuno ha questo ruolo.
                </Typography>
            )}

            {!error && !loading && users.length > 0 && (
                <TableContainer {...CONTENITORE_TABELLA}>
                    <Table size="small" sx={TABELLA_PAGINA}>
                        <TableHead>
                            <TableRow sx={TESTATA_TABELLA}>
                                <TableCell>Utente</TableCell>
                                <TableCell>Email</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.sub}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                {(user.displayName || '?').charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Typography variant="body2">{user.displayName}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{user.email}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

RoleUsersPanel.propTypes = {
    roleId: PropTypes.string.isRequired,
};
